// PillarTable/usePillarTableColumnsValues.tsx
import { isEqual } from "lodash";
import React, { useLayoutEffect, useRef, useState, useEffect } from "react";

import PillarTableColumn, {
  type PillarTableColumnProps,
} from "shared/components/pillar-table/columns/PillarTableColumn";
import { ColumnTypes } from "shared/components/pillar-table/custom-reports/ColumnTypesMapper";
import {
  type CustomReportColumnConfig,
  renderCustomReportColumn,
} from "shared/components/pillar-table/custom-reports/customReportColumnsMapper";

import { PillarDataTableFixedColumn } from "shared/components/pillar-table/helpers/addFixedStyleToColumn";
import { isPillarTableNestedTable } from "shared/components/pillar-table/nested/pillarTableNestedTableHelpers";
import { PaginationType } from "shared/components/pillar-table/PillarTableContext";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { useCustomSession } from "shared/context/CustomSessionContext";

// Helper function to recursively check if a component is part of PillarTableColumn hierarchy
const isPartOfPillarColumnHierarchy = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  componentType: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchObject: any = PillarTableColumn,
): boolean => {
  if (componentType === searchObject) {
    return true;
  }

  // Check all properties of the search object
  for (const key in searchObject) {
    if (
      Object.prototype.hasOwnProperty.call(searchObject, key) &&
      typeof searchObject[key] === "function"
    ) {
      if (componentType === searchObject[key]) {
        return true;
      }
      // Recursively check functions that might have nested properties
      // (like PillarTableColumnText.EditSelectMenu)
      if (
        typeof searchObject[key] === "function" &&
        searchObject[key] !== null
      ) {
        if (isPartOfPillarColumnHierarchy(componentType, searchObject[key])) {
          return true;
        }
      }
    }
  }

  return false;
};

// Helper function to check if a React element is a valid PillarTable column
const isPillarTableColumn = (child: React.ReactElement): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const componentType = child.type as any;

  // Check if it's part of the PillarTableColumn hierarchy
  if (typeof componentType === "function") {
    return isPartOfPillarColumnHierarchy(componentType);
  }

  return false;
};

interface UsePillarTableColumnsProps<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
> {
  children: React.ReactNode;
  displayColumns?: CustomReportColumnConfig[];
  pagination?: boolean | PaginationType;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const usePillarTableColumnsValues = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
>({
  children,
  displayColumns,
  pagination,
}: UsePillarTableColumnsProps<RowGeneric, ExtraPropsGeneric>) => {
  const { adminMode } = useCustomSession();
  const [fixedColumnWidths, setFixedColumnWidths] = useState<number[]>([]);
  const columnRefs = useRef<(HTMLDivElement | null)[]>([]);

  const isInfinityPagination = pagination === PaginationType.Infinity;

  // A table's children are a grab bag — header bar, filters, export buttons,
  // columns, nested tables. Only the last two describe a row, so this keeps
  // those and drops the rest; the other components find themselves elsewhere.
  let columns: React.ReactElement[] =
    // Children.map rather than a plain filter: it flattens fragments and keys
    // what it returns, so these elements stay renderable as bare arrays.
    React.Children.map(children, (child): React.ReactElement | null => {
      if (React.isValidElement(child) && isPillarTableColumn(child)) {
        return child;
      }
      // Nested tables ride along in the same list but aren't part of the
      // PillarTableColumn hierarchy — they render as their own full-width row.
      if (isPillarTableNestedTable(child)) {
        return child;
      }
      return null;
      // Children.map itself returns undefined when there are no children.
    })?.filter((e) => e !== null) ?? [];

  if (displayColumns?.length) {
    //TODO: The customReportColumnsMapper should be returning pillar table columns instead of string values. This entire thing should be less lines of code.
    columns = (displayColumns || [])
      .map(
        (
          customReportColumnConfig: CustomReportColumnConfig,
          columnIndex: number,
        ) => {
          if (
            !Object.keys(ColumnTypes).includes(
              customReportColumnConfig.columnType,
            )
          ) {
            console.warn(
              `Column type ${customReportColumnConfig.columnType} is not recognized.`,
            );
            return null;
          }

          if (!adminMode && customReportColumnConfig.adminOnly) {
            return null;
          }

          return renderCustomReportColumn(
            customReportColumnConfig,
            `export-column-${columnIndex}`,
          );
        },
      )
      .filter(
        (column): column is React.ReactElement =>
          column !== null && React.isValidElement(column),
      );
  }

  // Sort columns by fixedColumn property, but keep as function wrappers
  const sortColumns = (
    columns: Array<React.ReactElement<PillarTableColumnProps<RowGeneric>>>,
  ): Array<React.ReactElement<PillarTableColumnProps<RowGeneric>>> => {
    const priority = {
      [PillarDataTableFixedColumn.left]: -1,
      [PillarDataTableFixedColumn.right]: 1,
      default: 0,
    };
    return columns.slice().sort((a, b) => {
      const aElem = a;
      const bElem = b;
      const fixedA: PillarDataTableFixedColumn | "default" =
        aElem.props.fixedColumn || "default";
      const fixedB: PillarDataTableFixedColumn | "default" =
        bElem.props.fixedColumn || "default";
      return (
        (priority[fixedA] || priority.default) -
        (priority[fixedB] || priority.default)
      );
    });
  };
  columns = sortColumns(columns);

  const updateWidths = () => {
    const widths: number[] = columnRefs.current.map(
      (ref) => ref?.getBoundingClientRect().width || 0,
    );

    setFixedColumnWidths((prevWidths) =>
      isEqual(prevWidths, widths) ? prevWidths : widths,
    );
  };

  useLayoutEffect(() => {
    updateWidths();
  }, [displayColumns]);

  const firstColumn = columnRefs?.current?.[0];

  useEffect(() => {
    if (!isInfinityPagination || !firstColumn) {
      return;
    }

    const observer = new ResizeObserver(updateWidths);

    observer.observe(firstColumn);

    return () => {
      observer.disconnect();
    };
  }, [isInfinityPagination, firstColumn]);

  return {
    columns,
    fixedColumnWidths,
    columnRefs,
  };
};
